import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ComponentType, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router";
import { Layout } from "./components/Layout";
import { Spinner } from "./components/ui";
import "./index.css";
import { ApiError } from "./lib/api";
import { SessionProvider, useSession } from "./lib/auth";
import { LoginPage } from "./pages/Login";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      // Don't retry what retrying can't fix.
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    },
  },
});

function RequireSession() {
  const { signedIn, loading } = useSession();
  if (loading) return <Spinner />;
  return signedIn ? <Outlet /> : <Navigate to="/login" replace />;
}

// Pages load on first visit, so the initial bundle stays small.
const page = <M,>(load: () => Promise<M>, pick: (m: M) => ComponentType) => ({
  lazy: async () => ({ Component: pick(await load()) }),
  hydrateFallbackElement: <Spinner />,
});

const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  {
    element: <RequireSession />,
    children: [
      {
        element: <Layout />,
        children: [
          {
            index: true,
            ...page(
              () => import("./pages/Analyses"),
              (m) => m.AnalysesPage,
            ),
          },
          {
            path: "analyses/new",
            ...page(
              () => import("./pages/NewAnalysis"),
              (m) => m.NewAnalysisPage,
            ),
          },
          {
            path: "analyses/:id",
            ...page(
              () => import("./pages/AnalysisDetail"),
              (m) => m.AnalysisDetailPage,
            ),
          },
          {
            path: "reviews",
            ...page(
              () => import("./pages/Reviews"),
              (m) => m.ReviewsPage,
            ),
          },
          {
            path: "categories",
            ...page(
              () => import("./pages/Categories"),
              (m) => m.CategoriesPage,
            ),
          },
          {
            path: "settings",
            ...page(
              () => import("./pages/Settings"),
              (m) => m.SettingsPage,
            ),
          },
          {
            path: "api",
            ...page(
              () => import("./pages/ApiDocs"),
              (m) => m.ApiDocsPage,
            ),
          },
          {
            path: "platform",
            ...page(
              () => import("./pages/Platform"),
              (m) => m.PlatformPage,
            ),
          },
          { path: "*", element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <RouterProvider router={router} />
      </SessionProvider>
    </QueryClientProvider>
  </StrictMode>,
);
